import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Alert from '../components/ui/Alert';
import { useDocuments } from '../contexts/DocumentContext';
import { useAuth } from '../contexts/AuthContext';
import { checkMigrationData } from '../utils/migrationUtil';
import './HomePage.css';

// Structured data for SEO
const structuredData = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  "name": "Sign in Seconds",
  "applicationCategory": "BusinessApplication",
  "operatingSystem": "Web",
  "offers": {
    "@type": "Offer",
    "price": "29",
    "priceCurrency": "USD"
  },
  "description": "The fastest e-signature solution for small businesses. Upload, prepare, and send documents for signature in seconds, not minutes.",
};

const HomePage = () => {
  const { documents, loading } = useDocuments();
  const { isAuthenticated } = useAuth();
  const [migrationData, setMigrationData] = useState(null);
  const [isVisible, setIsVisible] = useState({});
  const [animationStep, setAnimationStep] = useState(0);
  const recentDocuments = documents.slice(0, 3);
  
  // Refs for intersection observer
  const heroRef = useRef(null);
  const featuresRef = useRef(null);
  const pricingRef = useRef(null);
  const ctaRef = useRef(null);
  
  // Check for data to migrate on mount
  useEffect(() => {
    const data = checkMigrationData();
    setMigrationData(data);
  }, []);
  
  // Set up intersection observer for animations
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };
    
    const observerCallback = (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setIsVisible(prev => ({ ...prev, [entry.target.id]: true }));
        }
      });
    };
    
    const observer = new IntersectionObserver(observerCallback, observerOptions);
    
    const refs = [
      { ref: heroRef, id: 'hero' },
      { ref: featuresRef, id: 'features' },
      { ref: pricingRef, id: 'pricing' },
      { ref: ctaRef, id: 'cta' }
    ];
    
    refs.forEach(({ ref, id }) => {
      if (ref.current) {
        observer.observe(ref.current);
      }
    });
    
    return () => {
      refs.forEach(({ ref }) => {
        if (ref.current) {
          observer.unobserve(ref.current);
        }
      });
    };
  }, []);

  // Animation steps cycle with a slightly longer duration for better readability
  useEffect(() => {
    const interval = setInterval(() => {
      setAnimationStep((prev) => (prev + 1) % 4);
    }, 4000);
    
    return () => clearInterval(interval);
  }, []);

  return (
    <main className="home-page">
      {/* Structured data for SEO */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
      
      {/* Migration alert removed as requested */}
      
      {/* Hero Section */}
      <section id="hero" ref={heroRef} className={`hero-section ${isVisible.hero ? 'animate-in' : ''}`}>
        <div className="hero-bg-gradient"></div>
        
        <div className="container">
          <div className="hero-content">
            <div className="hero-text">
              <h1 className="hero-title">
                E-signatures made <span className="text-gradient">simple</span> for small businesses
              </h1>
              
              <p className="hero-subtitle">
                The affordable DocuSign alternative designed specifically for small businesses. Get documents signed quickly and securely without the enterprise price tag.
              </p>
              
              <div className="hero-actions">
                <Link to={isAuthenticated ? "/dashboard" : "/signup"}>
                  <Button size="large" className="cta-button">
                    Start Today
                    <span className="arrow-icon">→</span>
                  </Button>
                </Link>
                
                <a href="#features" className="secondary-link">
                  See How It Works
                </a>
              </div>
              
              <div className="hero-stats">
                <div className="stat-card">
                  <div className="stat-value">93%</div>
                  <div className="stat-label">Time saved</div>
                </div>
                
                <div className="stat-card">
                  <div className="stat-value">100%</div>
                  <div className="stat-label">Small business focused</div>
                </div>
                
                <div className="stat-card">
                  <div className="stat-value">24/7</div>
                  <div className="stat-label">Support</div>
                </div>
              </div>
            </div>
            
            <div className="hero-animation">
              <div className="animation-card">
                <h3 className="animation-title">
                  Simple, Secure, and 
                  <span className="highlight">Legally Binding</span>
                </h3>
                
                <div className="progress-steps">
                  {[0, 1, 2, 3].map((step) => (
                    <div 
                      key={step} 
                      className={`progress-step ${animationStep >= step ? 'active' : ''} ${animationStep === step ? 'current' : ''}`}
                    ></div>
                  ))}
                </div>
                
                <div className="animation-content">
                  {/* Step 1: Upload */}
                  <div className={`animation-step ${animationStep === 0 ? 'visible' : 'hidden'}`}>
                    <div className="step-icon">
                      <span className="icon">📄</span>
                    </div>
                    <h4 className="step-title">Upload Documents</h4>
                    <p className="step-description">
                      Easily upload your documents in any format.
                    </p>
                    <div className="step-demo">
                      <div className="demo-file">
                        <span className="file-icon">📄</span>
                        <span className="file-name">Contract_Draft.pdf</span>
                        <span className="file-check">✓</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Step 2: Add Recipients */}
                  <div className={`animation-step ${animationStep === 1 ? 'visible' : 'hidden'}`}>
                    <div className="step-icon">
                      <span className="icon">✉️</span>
                    </div>
                    <h4 className="step-title">Add Recipients</h4>
                    <p className="step-description">
                      Add who needs to sign and in what order.
                    </p>
                    <div className="step-demo">
                      <div className="demo-recipients">
                        <div className="recipient">
                          <span className="recipient-number">1</span>
                          <span className="recipient-email">john@example.com</span>
                          <span className="recipient-role">Signer</span>
                        </div>
                        <div className="recipient">
                          <span className="recipient-number">2</span>
                          <span className="recipient-email">sarah@example.com</span>
                          <span className="recipient-role">Signer</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Step 3: Sign */}
                  <div className={`animation-step ${animationStep === 2 ? 'visible' : 'hidden'}`}>
                    <div className="step-icon">
                      <span className="icon">✍️</span>
                    </div>
                    <h4 className="step-title">Sign Documents</h4>
                    <p className="step-description">
                      Recipients sign with just a few clicks.
                    </p>
                    <div className="step-demo">
                      <div className="demo-signature">
                        <img 
                          src="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjUwIiB2aWV3Qm94PSIwIDAgMjAwIDUwIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPgogIDxwYXRoIGQ9Ik0xMCAxMCBDMjAgNSA0MCAyMCA1MCAxNSBDNjAgMTAgODAgMzAgOTAgMjUgQzEwMCAyMCAxMjAgNDAgMTMwIDM1IEMxNDAgMzAgMTYwIDEwIDE3MCAxNSBDMTgwIDIwIDE5MCAzMCAxOTAgMzUiIGZpbGw9Im5vbmUiIHN0cm9rZT0iIzY0NzZmZiIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiLz4KPC9zdmc+" 
                          alt="Signature" 
                          className="signature-image"
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Step 4: Complete */}
                  <div className={`animation-step ${animationStep === 3 ? 'visible' : 'hidden'}`}>
                    <div className="step-icon success">
                      <span className="icon">✓</span>
                    </div>
                    <h4 className="step-title">Complete & Secure</h4>
                    <p className="step-description">
                      All parties receive a legally binding copy.
                    </p>
                    <div className="step-demo">
                      <div className="demo-complete">
                        <div className="complete-file">
                          <span className="file-icon">📄</span>
                          <div className="file-info">
                            <span className="file-name">Contract_Signed.pdf</span>
                            <span className="file-status">Completed</span>
                          </div>
                          <span className="file-time">Now</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" ref={featuresRef} className={`features-section ${isVisible.features ? 'animate-in' : ''}`}>
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">
              Everything you need for <span className="text-primary">effortless</span> e-signatures
            </h2>
            <p className="section-subtitle">
              Our platform simplifies the e-signature process with powerful features designed specifically for small businesses.
            </p>
          </div>
          
          <div className="features-grid">
            <Card className="feature-card">
              <div className="feature-icon">
                <span className="icon">⚡</span>
              </div>
              <h3 className="feature-title">Quick Signatures</h3>
              <p className="feature-description">
                Get documents signed in seconds with our streamlined process and intuitive interface.
              </p>
            </Card>
            
            <Card className="feature-card">
              <div className="feature-icon">
                <span className="icon">🔒</span>
              </div>
              <h3 className="feature-title">Bank-Level Security</h3>
              <p className="feature-description">
                Your documents are protected with enterprise-grade encryption and security measures.
              </p>
            </Card>
            
            <Card className="feature-card">
              <div className="feature-icon">
                <span className="icon">⏱️</span>
              </div>
              <h3 className="feature-title">Save Time</h3>
              <p className="feature-description">
                Automate your document workflow and reduce processing time by up to 93%.
              </p>
            </Card>
            
            <Card className="feature-card">
              <div className="feature-icon">
                <span className="icon">👥</span>
              </div>
              <h3 className="feature-title">Team Collaboration</h3>
              <p className="feature-description">
                Easily collaborate with team members and track document status in real-time.
              </p>
            </Card>
            
            <Card className="feature-card">
              <div className="feature-icon">
                <span className="icon">🌐</span>
              </div>
              <h3 className="feature-title">Global Compliance</h3>
              <p className="feature-description">
                Our e-signatures are legally binding and compliant with ESIGN, UETA, and eIDAS.
              </p>
            </Card>
            
            <Card className="feature-card">
              <div className="feature-icon">
                <span className="icon">📱</span>
              </div>
              <h3 className="feature-title">Mobile Friendly</h3>
              <p className="feature-description">
                Sign documents from anywhere using our responsive mobile interface.
              </p>
            </Card>
            
            <Card className="feature-card">
              <div className="feature-icon">
                <span className="icon">🔄</span>
              </div>
              <h3 className="feature-title">Custom Workflows</h3>
              <p className="feature-description">
                Create customized signature workflows to match your business processes.
              </p>
            </Card>
            
            <Card className="feature-card">
              <div className="feature-icon">
                <span className="icon">☁️</span>
              </div>
              <h3 className="feature-title">Cloud Storage</h3>
              <p className="feature-description">
                Securely store all your signed documents in the cloud for easy access.
              </p>
            </Card>
          </div>
        </div>
      </section>
      
      {/* Pricing Section */}
      <section id="pricing" ref={pricingRef} className={`pricing-section ${isVisible.pricing ? 'animate-in' : ''}`}>
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Simple, Transparent Pricing</h2>
            <p className="section-subtitle">Choose the plan that works for your business</p>
          </div>
          
          <div className="pricing-cards">
            <Card className="pricing-card">
              <h3 className="pricing-title">Professional</h3>
              <div className="pricing-badge">Most Popular</div>
              <div className="pricing-price">
                <span className="price-currency">$</span>
                <span className="price-amount">29</span>
                <span className="price-period">/month</span>
              </div>
              <p className="pricing-description">
                Perfect for small businesses and professionals
              </p>
              
              <ul className="pricing-features">
                <li className="pricing-feature">
                  <span className="feature-check">✓</span>
                  <span className="feature-text">Unlimited documents</span>
                </li>
                <li className="pricing-feature">
                  <span className="feature-check">✓</span>
                  <span className="feature-text">All field types</span>
                </li>
                <li className="pricing-feature">
                  <span className="feature-check">✓</span>
                  <span className="feature-text">Document templates</span>
                </li>
                <li className="pricing-feature">
                  <span className="feature-check">✓</span>
                  <span className="feature-text">Up to 5 users</span>
                </li>
                <li className="pricing-feature">
                  <span className="feature-check">✓</span>
                  <span className="feature-text">Priority support</span>
                </li>
                <li className="pricing-feature">
                  <span className="feature-check">✓</span>
                  <span className="feature-text">Advanced security</span>
                </li>
              </ul>
              
              <Link to={isAuthenticated ? "/dashboard" : "/signup"} className="pricing-cta">
                <Button fullWidth>Start Today</Button>
              </Link>
            </Card>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section id="cta" ref={ctaRef} className={`cta-section ${isVisible.cta ? 'animate-in' : ''}`}>
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">Ready to streamline your document signing process?</h2>
            <p className="cta-description">
              Join small businesses everywhere who trust Sign in Seconds as their affordable e-signature solution.
            </p>
            <div className="cta-actions">
              <Link to={isAuthenticated ? "/dashboard" : "/signup"}>
                <Button size="large">Start Today</Button>
              </Link>
              <Link to="/login">
                <Button variant="secondary" size="large">Sign In</Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Recent Documents Section */}
      {documents.length > 0 && (
        <section className="recent-documents">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">Your Recent Documents</h2>
              <p className="section-subtitle">Quick access to your most recent work</p>
            </div>
            
            <div className="documents-grid">
              {recentDocuments.map(doc => (
                <Card 
                  key={doc.id} 
                  className="document-card"
                  title={doc.name || 'Untitled Document'}
                  subtitle={`Last updated: ${new Date(doc.updated_at || doc.updatedAt).toLocaleDateString()}`}
                  footer={
                    <div className="document-card-footer">
                      <span className={`document-status status-${doc.status}`}>
                        {doc.status}
                      </span>
                      <Link to={`/editor/${doc.id}`}>
                        <Button variant="tertiary" size="small">
                          View Document
                        </Button>
                      </Link>
                    </div>
                  }
                >
                  <p className="document-card-info">
                    {doc.recipientEmail || doc.recipient_email ? (
                      <span>Recipient: {doc.recipientEmail || doc.recipient_email}</span>
                    ) : (
                      <span>No recipient added yet</span>
                    )}
                  </p>
                </Card>
              ))}
            </div>
            
            <div className="text-center mt-lg">
              <div className="home-actions">
                <Link to="/upload">
                  <Button variant="secondary">Create New Document</Button>
                </Link>
                <Link to="/dashboard">
                  <Button variant="tertiary">View All Documents</Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}
    </main>
  );
};

export default HomePage;
